/**
 * FishingSystem - Manages fishing mechanics
 *
 * The player right-clicks while holding a fishing rod and looking at water.
 * A bobber appears, waits 5-30 seconds, then dips (bite).
 * The player right-clicks again within 2 seconds to catch.
 *
 * Loot table:
 *   Fish (60%): RAW_COD 60%, RAW_SALMON 25%, TROPICAL_FISH 10%, PUFFERFISH 5%
 *   Junk (30%): STICK 50%, STRING 30%, BONE 20%
 *   Treasure (10%): IRON_INGOT 40%, GOLD_INGOT 20%, DIAMOND_GEM 10%, ENDER_PEARL 10%, BOW 20%
 */

import * as THREE from 'three';
import { ItemId } from './ItemRegistry.js';

// Re-export for convenience
export const FISHING_ROD_ID = ItemId.FISHING_ROD;   // -78

// Fish item IDs (re-exported from ItemId for convenience)
export const FishItemId = {
  RAW_COD:        ItemId.RAW_COD,         // -79
  COOKED_COD:     ItemId.COOKED_COD,      // -80
  RAW_SALMON:     ItemId.RAW_SALMON,      // -81
  COOKED_SALMON:  ItemId.COOKED_SALMON,   // -82
  TROPICAL_FISH:  ItemId.TROPICAL_FISH,   // -83
  PUFFERFISH:     ItemId.PUFFERFISH,      // -84
};

// Fishing states
const STATE_IDLE = 0;
const STATE_CAST = 1;      // Bobber is in the water, waiting for a bite
const STATE_BITE = 2;       // Fish is biting, player has ~2 seconds to reel in
const STATE_REELING = 3;    // Reeling animation (brief)

// Bobber constants
const BOBBER_SIZE = 0.15;
const BOB_SPEED = 2.0;       // oscillation speed (rad/s)
const BOB_AMPLITUDE = 0.03;  // gentle bobbing amplitude
const BITE_DIP = 0.3;        // how far the bobber dips on bite
const BITE_WINDOW = 2.0;     // seconds the player has to reel in

// Wait time range (seconds)
const MIN_WAIT = 5;
const MAX_WAIT = 30;

// Durability
const FISHING_ROD_DURABILITY = 65;

export class FishingSystem {
  /**
   * @param {THREE.Scene} scene
   * @param {THREE.Camera} camera
   * @param {Object} world - World instance (has getBlock method)
   * @param {Object} inventory - Inventory instance
   * @param {Object} soundManager - SoundManager instance
   */
  constructor(scene, camera, world, inventory, soundManager) {
    this.scene = scene;
    this.camera = camera;
    this.world = world;
    this.inventory = inventory;
    this.sound = soundManager;

    this._state = STATE_IDLE;
    this._bobberGroup = null;   // THREE.Group for the bobber
    this._line = null;          // THREE.Line from player to bobber
    this._bobberPos = new THREE.Vector3();
    this._bobberBaseY = 0;      // water surface Y
    this._waitTimer = 0;        // countdown until fish bites
    this._biteTimer = 0;        // countdown for bite window
    this._reelTimer = 0;        // brief reel animation
    this._elapsed = 0;          // accumulated time for bobbing
    this._splashParticles = []; // simple splash effect
    this._catchResult = null;   // loot rolled on bite

    // Line material (thin dark line)
    this._lineMaterial = new THREE.LineBasicMaterial({ color: 0x333333, linewidth: 1 });
  }

  // ============================================================
  // PUBLIC API
  // ============================================================

  /**
   * Check if the fishing system is currently active (bobber out).
   */
  isActive() {
    return this._state !== STATE_IDLE;
  }

  /**
   * Attempt to start fishing or reel in.
   * Called on right-click when the player is holding a fishing rod.
   *
   * @param {THREE.Vector3} playerPos - Player eye position
   * @param {THREE.Vector3} lookDir - Normalized look direction
   * @returns {boolean} true if the action was consumed (don't pass to block placement)
   */
  startFishing(playerPos, lookDir) {
    if (this._state === STATE_IDLE) {
      return this._castLine(playerPos, lookDir);
    }
    if (this._state === STATE_BITE) {
      return this._reelIn();
    }
    // If cast but no bite yet, cancel and re-cast (or just cancel)
    if (this._state === STATE_CAST) {
      this.cancelFishing();
      return true;
    }
    return false;
  }

  /**
   * Update every frame.
   * @param {number} dt - delta time in seconds
   */
  update(dt) {
    if (this._state === STATE_IDLE) return;

    this._elapsed += dt;

    // Update splash particles
    this._updateSplashParticles(dt);

    if (this._state === STATE_CAST) {
      this._updateCast(dt);
    } else if (this._state === STATE_BITE) {
      this._updateBite(dt);
    } else if (this._state === STATE_REELING) {
      this._updateReel(dt);
    }

    // Update bobber visual position (bobbing)
    this._updateBobberVisual();

    // Update line from player hand to bobber
    this._updateLine();
  }

  /**
   * Cancel fishing (e.g. player switched items, opened inventory, etc.)
   */
  cancelFishing() {
    this._removeBobber();
    this._removeLine();
    this._clearSplashParticles();
    this._state = STATE_IDLE;
    this._catchResult = null;
  }

  // ============================================================
  // INTERNAL: CASTING
  // ============================================================

  _castLine(playerPos, lookDir) {
    // Raycast to find water surface within 20 blocks
    const waterHit = this._findWaterSurface(playerPos, lookDir, 20);
    if (!waterHit) return false; // Not looking at water

    // Place bobber at water surface
    this._bobberPos.copy(waterHit);
    this._bobberBaseY = waterHit.y;
    this._createBobber();
    this._createLine();

    // Set wait timer (random 5-30 seconds)
    this._waitTimer = MIN_WAIT + Math.random() * (MAX_WAIT - MIN_WAIT);
    this._elapsed = 0;
    this._state = STATE_CAST;

    // Play a small splash sound
    if (this.sound && this.sound.playSplash) {
      this.sound.playSplash();
    }

    return true;
  }

  /**
   * Raycast to find the first WATER block surface along the look direction.
   * Returns the world position of the water surface, or null.
   */
  _findWaterSurface(origin, direction, maxDist) {
    // Water block ID = 7
    const WATER = 7;
    const AIR = 0;

    let x = Math.floor(origin.x);
    let y = Math.floor(origin.y);
    let z = Math.floor(origin.z);

    const stepX = direction.x >= 0 ? 1 : -1;
    const stepY = direction.y >= 0 ? 1 : -1;
    const stepZ = direction.z >= 0 ? 1 : -1;

    const tDeltaX = direction.x !== 0 ? Math.abs(1 / direction.x) : Infinity;
    const tDeltaY = direction.y !== 0 ? Math.abs(1 / direction.y) : Infinity;
    const tDeltaZ = direction.z !== 0 ? Math.abs(1 / direction.z) : Infinity;

    let tMaxX = direction.x !== 0
      ? (direction.x >= 0 ? (x + 1 - origin.x) : (x - origin.x)) / direction.x
      : Infinity;
    let tMaxY = direction.y !== 0
      ? (direction.y >= 0 ? (y + 1 - origin.y) : (y - origin.y)) / direction.y
      : Infinity;
    let tMaxZ = direction.z !== 0
      ? (direction.z >= 0 ? (z + 1 - origin.z) : (z - origin.z)) / direction.z
      : Infinity;

    let t = 0;

    while (t < maxDist) {
      const block = this.world.getBlock(x, y, z);

      // Found water
      if (block === WATER) {
        // Return the top surface of the water block
        return new THREE.Vector3(x + 0.5, y + 0.9, z + 0.5);
      }

      // Hit a solid block before reaching water -- no fishing here
      if (block !== AIR && block !== WATER) {
        return null;
      }

      // Step to next voxel
      if (tMaxX < tMaxY) {
        if (tMaxX < tMaxZ) {
          t = tMaxX; x += stepX; tMaxX += tDeltaX;
        } else {
          t = tMaxZ; z += stepZ; tMaxZ += tDeltaZ;
        }
      } else {
        if (tMaxY < tMaxZ) {
          t = tMaxY; y += stepY; tMaxY += tDeltaY;
        } else {
          t = tMaxZ; z += stepZ; tMaxZ += tDeltaZ;
        }
      }
    }

    return null;
  }

  // ============================================================
  // INTERNAL: STATE UPDATES
  // ============================================================

  _updateCast(dt) {
    this._waitTimer -= dt;
    if (this._waitTimer <= 0) {
      // Fish bites!
      this._onBite();
    }
  }

  _onBite() {
    this._state = STATE_BITE;
    this._biteTimer = BITE_WINDOW;

    // Roll loot now so we know what was caught
    this._catchResult = this._rollLoot();

    // Visual: dip the bobber
    // (handled in _updateBobberVisual via state check)

    // Splash particles
    this._spawnSplashParticles();

    // Splash sound
    if (this.sound && this.sound.playSplash) {
      this.sound.playSplash();
    }
  }

  _updateBite(dt) {
    this._biteTimer -= dt;
    if (this._biteTimer <= 0) {
      // Player missed the bite -- cancel
      this.cancelFishing();
    }
  }

  _reelIn() {
    if (!this._catchResult) {
      this.cancelFishing();
      return true;
    }

    // Give item to player
    const loot = this._catchResult;
    if (loot.itemId !== undefined) {
      this.inventory.addItem(loot.itemId, loot.count);
    } else if (loot.blockId !== undefined) {
      for (let i = 0; i < loot.count; i++) {
        this.inventory.addBlock(loot.blockId);
      }
    }

    // Damage the fishing rod
    this._damageFishingRod();

    // Brief reel animation
    this._state = STATE_REELING;
    this._reelTimer = 0.3;

    // Sound
    if (this.sound && this.sound.playSplash) {
      this.sound.playSplash();
    }

    return true;
  }

  _updateReel(dt) {
    this._reelTimer -= dt;
    if (this._reelTimer <= 0) {
      this.cancelFishing();
    }
  }

  // ============================================================
  // INTERNAL: FISHING ROD DURABILITY
  // ============================================================

  _damageFishingRod() {
    const slot = this.inventory.getSelectedSlot();
    if (!slot) return;

    // The fishing rod is stored as { itemId: FISHING_ROD_ID, count: 1, durability, maxDurability }
    if (slot.itemId === FISHING_ROD_ID && slot.durability !== undefined) {
      slot.durability -= 1;
      if (slot.durability <= 0) {
        // Rod breaks
        this.inventory.slots[this.inventory.selectedIndex] = null;
        if (this.sound && this.sound.playBreak) {
          this.sound.playBreak();
        }
      }
    }
  }

  // ============================================================
  // INTERNAL: LOOT TABLE
  // ============================================================

  _rollLoot() {
    const roll = Math.random();

    if (roll < 0.60) {
      // Fish (60%)
      return this._rollFish();
    } else if (roll < 0.90) {
      // Junk (30%)
      return this._rollJunk();
    } else {
      // Treasure (10%)
      return this._rollTreasure();
    }
  }

  _rollFish() {
    const r = Math.random();
    if (r < 0.60) return { itemId: FishItemId.RAW_COD, count: 1 };
    if (r < 0.85) return { itemId: FishItemId.RAW_SALMON, count: 1 };
    if (r < 0.95) return { itemId: FishItemId.TROPICAL_FISH, count: 1 };
    return { itemId: FishItemId.PUFFERFISH, count: 1 };
  }

  _rollJunk() {
    const r = Math.random();
    if (r < 0.50) return { itemId: ItemId.STICK, count: 1 };
    if (r < 0.80) return { itemId: ItemId.STRING, count: 1 };
    return { itemId: ItemId.BONE, count: 1 };
  }

  _rollTreasure() {
    const r = Math.random();
    if (r < 0.40) return { itemId: ItemId.IRON_INGOT, count: 1 };
    if (r < 0.60) return { itemId: ItemId.GOLD_INGOT, count: 1 };
    if (r < 0.70) return { itemId: ItemId.DIAMOND_GEM, count: 1 };
    if (r < 0.80) return { itemId: ItemId.ENDER_PEARL, count: 1 };
    return { itemId: ItemId.BOW, count: 1 };
  }

  // ============================================================
  // INTERNAL: BOBBER VISUALS
  // ============================================================

  _createBobber() {
    this._removeBobber();

    this._bobberGroup = new THREE.Group();

    // Red/white bobber - bottom half red, top half white
    const geoBottom = new THREE.BoxGeometry(BOBBER_SIZE, BOBBER_SIZE * 0.5, BOBBER_SIZE);
    const matBottom = new THREE.MeshBasicMaterial({ color: 0xCC2222 });
    const meshBottom = new THREE.Mesh(geoBottom, matBottom);
    meshBottom.position.y = -BOBBER_SIZE * 0.25;

    const geoTop = new THREE.BoxGeometry(BOBBER_SIZE, BOBBER_SIZE * 0.5, BOBBER_SIZE);
    const matTop = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
    const meshTop = new THREE.Mesh(geoTop, matTop);
    meshTop.position.y = BOBBER_SIZE * 0.25;

    this._bobberGroup.add(meshBottom);
    this._bobberGroup.add(meshTop);
    this._bobberGroup.position.copy(this._bobberPos);

    this.scene.add(this._bobberGroup);
  }

  _removeBobber() {
    if (this._bobberGroup) {
      this.scene.remove(this._bobberGroup);
      // Dispose geometries and materials
      this._bobberGroup.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this._bobberGroup = null;
    }
  }

  _updateBobberVisual() {
    if (!this._bobberGroup) return;

    let yOffset = Math.sin(this._elapsed * BOB_SPEED) * BOB_AMPLITUDE;

    // During bite, dip the bobber down
    if (this._state === STATE_BITE) {
      // Smooth dip that oscillates
      const dipProgress = 1 - (this._biteTimer / BITE_WINDOW);
      const dipAmount = BITE_DIP * Math.sin(dipProgress * Math.PI * 4) * (1 - dipProgress * 0.5);
      yOffset -= Math.abs(dipAmount) * 0.5 + BITE_DIP * 0.5;
    }

    // During reeling, pull bobber toward player
    if (this._state === STATE_REELING) {
      const pullProgress = 1 - (this._reelTimer / 0.3);
      const playerPos = this.camera.position.clone();
      this._bobberGroup.position.lerpVectors(this._bobberPos, playerPos, pullProgress);
      return;
    }

    this._bobberGroup.position.set(
      this._bobberPos.x,
      this._bobberBaseY + yOffset,
      this._bobberPos.z
    );
  }

  // ============================================================
  // INTERNAL: FISHING LINE
  // ============================================================

  _createLine() {
    this._removeLine();

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(6); // 2 points x 3 components
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    this._line = new THREE.Line(geometry, this._lineMaterial);
    this.scene.add(this._line);
  }

  _removeLine() {
    if (this._line) {
      this.scene.remove(this._line);
      if (this._line.geometry) this._line.geometry.dispose();
      this._line = null;
    }
  }

  _updateLine() {
    if (!this._line || !this._bobberGroup) return;

    // Player hand position (slightly below and in front of camera)
    const camPos = this.camera.position.clone();
    const camDir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const handPos = camPos.clone()
      .add(camDir.multiplyScalar(0.5))
      .add(new THREE.Vector3(0, -0.3, 0));

    const bobberWorldPos = this._bobberGroup.position;

    const posAttr = this._line.geometry.getAttribute('position');
    posAttr.setXYZ(0, handPos.x, handPos.y, handPos.z);
    posAttr.setXYZ(1, bobberWorldPos.x, bobberWorldPos.y, bobberWorldPos.z);
    posAttr.needsUpdate = true;
  }

  // ============================================================
  // INTERNAL: SPLASH PARTICLES
  // ============================================================

  _spawnSplashParticles() {
    this._clearSplashParticles();

    const count = 8 + Math.floor(Math.random() * 5);
    for (let i = 0; i < count; i++) {
      const geo = new THREE.BoxGeometry(0.06, 0.06, 0.06);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x4488CC,
        transparent: true,
        opacity: 0.8,
      });
      const mesh = new THREE.Mesh(geo, mat);

      mesh.position.set(
        this._bobberPos.x + (Math.random() - 0.5) * 0.5,
        this._bobberBaseY + 0.1,
        this._bobberPos.z + (Math.random() - 0.5) * 0.5
      );

      const particle = {
        mesh,
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          1.5 + Math.random() * 2,
          (Math.random() - 0.5) * 2
        ),
        life: 0.5 + Math.random() * 0.5,
      };

      this._splashParticles.push(particle);
      this.scene.add(mesh);
    }
  }

  _updateSplashParticles(dt) {
    for (let i = this._splashParticles.length - 1; i >= 0; i--) {
      const p = this._splashParticles[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.scene.remove(p.mesh);
        p.mesh.geometry.dispose();
        p.mesh.material.dispose();
        this._splashParticles.splice(i, 1);
        continue;
      }

      // Apply gravity
      p.velocity.y -= 9.8 * dt;
      p.mesh.position.addScaledVector(p.velocity, dt);

      // Fade out
      p.mesh.material.opacity = Math.max(0, p.life / 1.0);
    }
  }

  _clearSplashParticles() {
    for (const p of this._splashParticles) {
      this.scene.remove(p.mesh);
      p.mesh.geometry.dispose();
      p.mesh.material.dispose();
    }
    this._splashParticles = [];
  }

  // ============================================================
  // STATIC HELPERS
  // ============================================================

  /**
   * Create a new fishing rod item for inventory.
   */
  static createFishingRod() {
    return {
      itemId: FISHING_ROD_ID,
      count: 1,
      durability: FISHING_ROD_DURABILITY,
      maxDurability: FISHING_ROD_DURABILITY,
    };
  }

  /**
   * Check if an inventory slot holds a fishing rod.
   */
  static isFishingRod(slot) {
    return slot && slot.itemId === FISHING_ROD_ID;
  }
}
